import { useState } from "react";
import { cn } from "../../lib/cn";
import { relativeShort } from "../../lib/format";
import type { Verification, VerificationRecord } from "../../lib/types";
import { Check, ChevronDown, ChevronRight, X } from "../shared/Icon";

const VERDICT_COLOR: Record<string, string> = {
  pass: "var(--status-success)",
  fail: "var(--status-error)",
  misconfigured: "var(--status-warning)",
  inconclusive: "var(--status-paused)",
  missing: "var(--text-tertiary)",
};

const KIND_LABEL: Record<VerificationRecord["kind"], string> = {
  gate: "Gate",
  mutation: "Revert check",
  verifier: "Independent review",
  secret_scan: "Secret scan",
  override: "Override",
  scenario: "Hidden scenarios",
};

/** Evidence for a task's branch: what passed, what didn't, why not auto-merged. */
export function EvidenceBody({ v }: { v: Verification }) {
  const head = v.head_sha?.slice(0, 10);
  return (
    <section className="space-y-3" aria-label="Verification evidence">
      <div className="flex items-baseline gap-2 text-small">
        <span className="text-text-secondary">Latest commit</span>
        {head && <code className="text-mono text-caption text-text-tertiary">{head}</code>}
        <span
          className="text-mono text-caption"
          style={{ color: VERDICT_COLOR[v.head_verdict ?? "missing"] }}
        >
          {v.head_verdict ?? "no evidence"}
        </span>
        {v.fix_rounds > 0 && (
          <span className="text-caption text-text-tertiary">
            · {v.fix_rounds} fix round{v.fix_rounds === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {v.blocked.length > 0 && (
        <ul className="space-y-0.5 text-small text-status-error" aria-label="Merge blocked">
          {v.blocked.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}

      {v.criteria.length > 0 && (
        <ul className="space-y-1" aria-label="Acceptance criteria">
          {v.criteria.map((c) => (
            <li key={c.criterion} className="flex items-start gap-2 text-small">
              <span
                title={c.met ? "met" : "not met"}
                style={{ color: c.met ? "var(--status-success)" : "var(--status-error)" }}
              >
                {c.met ? <Check size={14} /> : <X size={14} />}
              </span>
              <span className="text-text-secondary">{c.criterion}</span>
            </li>
          ))}
        </ul>
      )}

      {v.automerge_decision && (
        <div className="text-small text-text-tertiary">
          {v.automerge_decision === "queued" && "Queued for auto-merge."}
          {v.automerge_decision === "shadow" && "Shadow mode: would have auto-merged."}
          {v.automerge_decision === "vetoed" && (
            <>
              Not auto-merged: {v.automerge_block_reasons.join("; ")}
            </>
          )}
        </div>
      )}

      <ol className="divide-y divide-border-subtle" aria-label="Evidence timeline">
        {v.records.map((r) => (
          <RecordRow key={r.id} r={r} stale={!!v.head_sha && r.commit_sha !== v.head_sha} />
        ))}
      </ol>
    </section>
  );
}

function RecordRow({ r, stale }: { r: VerificationRecord; stale: boolean }) {
  const [open, setOpen] = useState(false);
  const findings = r.findings.filter((f): f is string => typeof f === "string");
  const hasDetail = !!r.excerpt || findings.length > 0 || r.not_checked.length > 0 || !!r.command;
  return (
    <li className={cn("py-1.5", stale && "opacity-60")}>
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left text-small"
        onClick={() => setOpen((o) => !o)}
        disabled={!hasDetail}
        aria-expanded={open}
      >
        <span className="w-3 text-text-tertiary">
          {hasDetail && (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </span>
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ background: VERDICT_COLOR[r.verdict] }}
          title={r.verdict}
        />
        <span className="text-text-primary">{KIND_LABEL[r.kind]}</span>
        <span className="text-mono text-caption" style={{ color: VERDICT_COLOR[r.verdict] }}>
          {r.verdict}
        </span>
        {r.commit_sha && (
          <code className="text-mono text-caption text-text-quaternary">
            {r.commit_sha.slice(0, 7)}
            {stale && " (older commit)"}
          </code>
        )}
        <span className="flex-1" />
        <span className="text-caption text-text-quaternary">{relativeShort(r.created_at)}</span>
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-5 text-caption text-text-tertiary">
          {r.reason && <div>Reason: {r.reason} ({r.actor})</div>}
          {r.command && <code className="block text-mono break-all">{r.command}</code>}
          {r.exit_code !== null && <div>Exit code {r.exit_code}</div>}
          {r.checked.length > 0 && <div>Checked: {r.checked.join(", ")}</div>}
          {r.not_checked.length > 0 && <div>Not checked: {r.not_checked.join(", ")}</div>}
          {findings.length > 0 && (
            <ul className="list-disc list-inside">
              {findings.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          )}
          {r.excerpt && (
            <pre className="max-h-48 overflow-auto rounded-sm bg-bg-inset p-2 text-mono whitespace-pre-wrap">
              {r.excerpt}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
